/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { notificationMastersService } from '../../services/notification-masters.service';
import { notificationTypesService } from '../../services/notification-types.service';
import { schedulesService } from '../../services/schedules.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate, useMyPermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import RaiseEventDialog from './raise-event-dialog';
import { JOB_NAMES } from '../../constants/jobNames';
import ScheduleStateTag from '../../components/common/schedule-state-tag';
import { zoneLabel } from '../../utils/timezones';
import { describeTiming } from '../../utils/schedule-timing';

const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '-');

const Field = ({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) => (
  <div>
    <label className="text-xs text-ink-muted block mb-0.5">{label}</label>
    <p className="text-sm text-ink">{value}</p>
    {hint ? <span className="text-xs text-gray-400">{hint}</span> : null}
  </div>
);

const ScheduleView = () => {
  const { id, isReady, navigateToList, navigateToEdit } = useSecureNavigation({ entity: 'schedule', basePath: '/schedules' });
  const canUpdate = useCanUpdate(JOB_NAMES.SCHEDULE_MASTER);
  const { has } = useMyPermissions();
  const canPublishEvent = has(PERMISSIONS.NOTIFICATION_EVENT_PUBLISH);
  const [raising, setRaising] = useState(false);

  // Same query keys the form's picker uses, so this is a cache read rather than
  // two more requests: a schedule names its target by id, and an id is not an
  // answer to "which notification does this fire?".
  const { data: notificationsPage } = useQuery({
    queryKey: ['notificationMasters', 'targetPicker'],
    queryFn: () => notificationMastersService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });
  const { data: typesPage } = useQuery({
    queryKey: ['notificationTypes', 'targetPicker'],
    queryFn: () => notificationTypesService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['schedule', id],
    queryFn: () => schedulesService.getById(id!),
    enabled: isReady && !!id,
  });

  // "When will this actually run?" — computed from the saved configuration, so
  // an expression that produces nothing shows as an empty list rather than as a
  // schedule that merely looks fine.
  const { data: preview } = useQuery({
    queryKey: ['schedule', id, 'preview'],
    queryFn: () => schedulesService.preview(id!, 10),
    enabled: isReady && !!id && !!data,
    retry: false,
  });

  // A notification has no code of its own — it inherits its type's — so the
  // types are read too, and the id is shown when neither resolves.
  const targetId = String((data as any)?.masterId || '');
  const target = ((notificationsPage as any)?.data || [])
    .find((n: any) => n._id === targetId);
  const targetLabel = target
    ? (((typesPage as any)?.data || [])
        .find((t: any) => t._id === String(target.notificationTypeId))?.notificationCode
       || target.description || targetId)
    : (targetId || '-');

  if (isLoading) return <PageSkeleton />;
  if (isError || !data) return (
    <div className="p-4">
      <Message severity="error" text={extractErrorMessage(error, 'Schedule not found')} className="mb-4 w-full" />
      <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigateToList()} />
    </div>
  );

  const runs = (preview?.runs || []) as string[];

  return (
    <div className="p-3" data-testid="SCR-Schedule-View">
      <ScreenHeader icon="pi pi-calendar" title="Schedule" onBack={() => navigateToList()} />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <h2 className="text-base font-semibold text-ink font-mono">{data.scheduleCode}</h2>
            <Tag value={data.masterType} severity={data.masterType === 'NOTIFICATION' ? 'info' : 'secondary'} className="text-xs px-1.5 py-0.5" />
            <Tag value={data.scheduleType} severity="secondary" className="text-xs px-1.5 py-0.5" />
            <ScheduleStateTag schedule={data} />
          </div>
        </div>
        <div className="flex gap-2">
          {/* An EVENT schedule is never polled — it waits for its key to be
              published — so raising that key is the only way to see whether it
              is wired to the key it claims. The dialog checks first and
              messages nobody until a second, deliberate press. Its own
              permission, because firing real notifications is not the same
              right as editing when they fire. */}
          {canPublishEvent && data.scheduleType === 'EVENT' && data.eventKey && !data.isDelete && (
            <Button label="Raise event" icon="pi pi-bolt" size="small" severity="secondary" outlined
              onClick={() => setRaising(true)} testId="ScheduleView.Button.RaiseEvent" />
          )}
          {canUpdate && !data.isDelete && <Button label="Edit" icon="pi pi-pencil" size="small" onClick={() => id && navigateToEdit(id)} />}
        </div>
      </div>

      {raising && (
        <RaiseEventDialog
          eventKey={data.eventKey || ''}
          scheduleCode={data.scheduleCode}
          masterId={data.masterId ? String(data.masterId) : undefined}
          parameters={data.parameters || undefined}
          onClose={() => setRaising(false)}
          // A fire stamps Last run, so this page is stale the moment it succeeds.
          onFired={() => { void refetch(); }}
        />
      )}

      {data.isActive === false && (
        <Message severity="warn" className="w-full mb-2"
          text="Deactivated, so it never runs — whatever the enable flag says." />
      )}
      {data.isActive !== false && !data.isScheduleEnabled && (
        <Message severity="info" className="w-full mb-2"
          text="Paused. Enable it to resume; the next fire time is recalculated from now." />
      )}

      <div className="ds-card p-3 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3 mb-3">
        <Field label="Notification Master" value={<span className="font-mono text-xs">{targetLabel}</span>} />
        <Field label="When" value={<span className="font-mono text-xs">{describeTiming(data)}</span>} />
        <Field label="Next run" value={fmt(data.nextRunAt)} />
        <Field label="Last run" value={fmt(data.lastRunAt)} />
        <Field label="Event key" value={data.eventKey || '-'} />
        {data.scheduleType === 'CRON' && (
          <Field label="Timezone" value={zoneLabel(data.timezone)}
            hint="The wall clock the expression is read in." />
        )}
        <Field
          label="Occurrences"
          value={data.maxOccurrences ? `${data.occurrenceCount ?? 0} of ${data.maxOccurrences}` : `${data.occurrenceCount ?? 0} (unlimited)`}
        />
        <div className="sm:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-ink-muted block mb-0.5">Type config</label>
            <pre className="text-sm text-ink whitespace-pre-wrap break-words font-mono">{JSON.stringify(data.typeConfig || {}, null, 2)}</pre>
          </div>
          <div>
            <label className="text-xs text-ink-muted block mb-0.5">Parameters</label>
            <pre className="text-sm text-ink whitespace-pre-wrap break-words font-mono">{JSON.stringify(data.parameters || {}, null, 2)}</pre>
          </div>
        </div>
      </div>

      <div className="ds-card p-3">
        <label className="text-xs text-ink-muted block mb-1">Next fire times</label>
        {runs.length ? (
          <ol className="text-sm text-ink flex flex-col gap-0.5">
            {runs.map((r, i) => <li key={i} className="font-mono text-xs">{fmt(r)}</li>)}
          </ol>
        ) : (
          <span className="text-sm text-gray-400">
            No upcoming runs — an EVENT schedule has none by design; otherwise check the type config.
          </span>
        )}
      </div>
    </div>
  );
};

export default ScheduleView;
